import type { JSX, ParentComponent } from "solid-js";
import { mergeProps, splitProps } from "solid-js";
import "./Stack.css";

type Props = {
	direction?: "row" | "column" | "row-reverse" | "column-reverse";
	/**
	 * Gap between children (`gap` from `Stack.css`).
	 *
	 * Rem sizes (typically 16px root):
	 *
	 * - `none` — `0`
	 * - `small` — `0.25rem`
	 * - `medium` — `0.5rem`
	 * - `intermediate` — `0.75rem`
	 * - `large` — `1rem`
	 * - `xlarge` — `1.5rem`
	 */
	spacing?:
		| "none"
		| "small"
		| "medium"
		| "intermediate"
		| "large"
		| "xlarge"
		| "xxlarge";
	align?: "start" | "center" | "end" | "stretch" | "baseline";
	justify?: "start" | "center" | "end" | "space-between" | "space-around";
	wrap?: boolean;
	fullWidth?: boolean;
	fullHeight?: boolean;
	/**
	 * Grow and shrink in a parent flex layout. Sets `flex: 1 1 0` and
	 * `min-width` / `min-height: 0` so overflow (e.g. text ellipsis) can work.
	 */
	grow?: boolean;
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	id?: string;
	ref?: (el: HTMLDivElement) => void;
	"aria-label"?: string;
	onDragEnter?: JSX.EventHandlerUnion<HTMLDivElement, DragEvent>;
	onDragLeave?: JSX.EventHandlerUnion<HTMLDivElement, DragEvent>;
	onDragOver?: JSX.EventHandlerUnion<HTMLDivElement, DragEvent>;
	onDrop?: JSX.EventHandlerUnion<HTMLDivElement, DragEvent>;
	role?: JSX.HTMLAttributes<HTMLDivElement>["role"];
};

type AxisStackProps = Omit<Props, "direction" | "wrap">;
type HStackProps = Omit<Props, "direction">;

/**
 * Flexbox layout with consistent spacing, direction, and alignment.
 *
 * [Design System →](http://localhost:5174/component/Stack)
 */
export const Stack: ParentComponent<Props> = (props) => {
	const merged = mergeProps(
		{
			direction: "column",
			spacing: "medium",
			wrap: false,
			fullWidth: false,
			fullHeight: false,
			grow: false,
		} as const,
		props,
	);
	const [local, rest] = splitProps(merged, [
		"direction",
		"spacing",
		"align",
		"justify",
		"wrap",
		"fullWidth",
		"fullHeight",
		"grow",
		"class",
		"children",
	]);

	const classes = () =>
		[
			"gvtui-stack",
			`gvtui-stack--${local.direction}`,
			`gvtui-stack--spacing-${local.spacing}`,
			local.align && `gvtui-stack--align-${local.align}`,
			local.justify && `gvtui-stack--justify-${local.justify}`,
			local.wrap && "gvtui-stack--wrap",
			local.fullWidth && "gvtui-stack--full-width",
			local.fullHeight && "gvtui-stack--full-height",
			local.grow && "gvtui-stack--grow",
			local.class,
		]
			.filter(Boolean)
			.join(" ");

	return (
		<div class={classes()} {...rest}>
			{local.children}
		</div>
	);
};

/**
 * Horizontal stack — always `direction="row"`. Wrapping is off by default.
 */
export const HStack: ParentComponent<HStackProps> = (props) => {
	const merged = mergeProps({ spacing: "none", wrap: false } as const, props);
	return <Stack direction="row" {...merged} />;
};

/**
 * Vertical stack — always `direction="column"` with wrapping disabled.
 */
export const VStack: ParentComponent<AxisStackProps> = (props) => {
	const merged = mergeProps({ spacing: "none" } as const, props);
	return <Stack direction="column" wrap={false} {...merged} />;
};

/**
 * Heaading stack — always `direction="column"`, full width, with wrapping disabled.
 */
export const HeadingStack: ParentComponent<AxisStackProps> = (props) => {
	const merged = mergeProps({ spacing: "small" } as const, props);
	return <Stack direction="column" wrap={false} fullWidth {...merged} />;
};
